import dayjs from 'dayjs';
import { useEffect, useMemo, useReducer } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import type {
  NumberingSeriesAmountOfDigits,
  NumberingSeriesResetInterval,
  NumberingSeriesSeparator,
  NumberingSeriesYearMonthFormat,
} from '~/types/user';

import { useUpdateNumberingSeries } from '~/api/numberingSeries';
import { useUser } from '~/api/user';
import { Blocker, Button, Icon } from '~/components';
import { Checkbox, Field, Form, Input, NumberInput, Select } from '~/components/Form';
import { statusColorThemes } from '~/styles/themes.css';
import { clsx } from '~/styles/utils';
import { filterBoolean, insertIf } from '~/utils/arrays';
import { generateEntryNumber, isNotBlank } from '~/utils/string';
import { toast } from '~/utils/toast';

import type { EditNumberingSeriesFormProps, EditNumberingSeriesFormType } from './types';

import {
  actionsStyle,
  editFormStyle,
  fieldsetStyle,
  formatFieldsStyle,
  formatRowStyle,
  lengthWarningStyle,
  nameAndTypeStyle,
  resetIntervalRowStyle,
  startAtLabelStyle,
  startAtStyle,
  suffixFieldsStyle,
  useSuffixStyle,
} from './NumberingSeries.css';
import { getNumberingSeriesEnabledFor, getNumberingSeriesType, isDuplicateNumberingSeriesFormat } from './utils';

const typeOptions = ['invoices', 'credit_notes', 'both'] as const;
const separatorOptions = [null, '_', '-', '.', '/'] satisfies (NumberingSeriesSeparator | null)[];
const yearMonthOptions = [null, 'YYYY', 'YY', 'YYYYMM', 'YYMM'] satisfies (NumberingSeriesYearMonthFormat | null)[];
const amountOfDigitsOptions = [2, 3, 4, 5, 6] satisfies (NumberingSeriesAmountOfDigits | null)[];

export const EditNumberingSeriesForm = ({ numberingSeries, onClose, section }: EditNumberingSeriesFormProps) => {
  const { data: user } = useUser();
  const mutation = useUpdateNumberingSeries(numberingSeries.id);

  const {
    control,
    formState: { isDirty },
    handleSubmit,
    setError,
    setValue,
    watch,
  } = useForm<EditNumberingSeriesFormType>({
    defaultValues: {
      name: numberingSeries.name,
      type: getNumberingSeriesType(numberingSeries),
      nextNumber: numberingSeries.nextNumber,
      resetInterval: numberingSeries.resetInterval,
      format: {
        amountOfDigits: numberingSeries.format.amountOfDigits,
        prefixSeparator: numberingSeries.format.prefixSeparator,
        prefixText: numberingSeries.format.prefixText ?? '',
        prefixYearMonth: numberingSeries.format.prefixYearMonth,
        suffixSeparator: numberingSeries.format.suffixSeparator,
        suffixText: numberingSeries.format.suffixText ?? '',
        suffixYearMonth: numberingSeries.format.suffixYearMonth,
      },
    },
  });

  const { t } = useTranslation(['common', 'settings', 'validation']);

  const watchNextNumber = watch('nextNumber');
  const watchFormat = watch('format');
  const watchPrefixYearMonth = watch('format.prefixYearMonth');
  const watchSuffixYearMonth = watch('format.suffixYearMonth');
  const watchResetInterval = watch('resetInterval');

  const hasSuffix =
    !!numberingSeries.format.suffixSeparator || !!numberingSeries.format.suffixYearMonth || !!numberingSeries.format.suffixText;
  const [showSuffix, toggleShowSuffix] = useReducer((prev) => !prev, hasSuffix);
  const previewEntryNumber = generateEntryNumber({ format: watchFormat, nextNumber: watchNextNumber });

  const allowMonthlyReset = useMemo(
    () => [watchPrefixYearMonth, watchSuffixYearMonth].includes('YYYYMM') || [watchPrefixYearMonth, watchSuffixYearMonth].includes('YYMM'),
    [watchPrefixYearMonth, watchSuffixYearMonth],
  );

  const resetIntervalOptions = useMemo(
    () => (['never', 'yearly', ...insertIf(allowMonthlyReset, 'monthly')] satisfies NumberingSeriesResetInterval[]).filter(filterBoolean),
    [allowMonthlyReset],
  );

  const typeItems = useMemo(
    () => typeOptions.map((type) => ({ label: t(`settings:fields.numberingSeries.type.options.${type}`), value: type })),
    [t],
  );
  const separatorItems = useMemo(
    () =>
      separatorOptions.map((separator) => ({
        label: separator ?? t('settings:fields.numberingSeries.format.separator.none'),
        value: separator,
      })),
    [t],
  );
  const yearMonthItems = useMemo(
    () =>
      yearMonthOptions.map((yearMonth) => ({
        label: t(`settings:fields.numberingSeries.format.year.options.${yearMonth || 'none'}`, {
          yearMonth: dayjs().format(yearMonth ?? ''),
        }),
        value: yearMonth,
      })),
    [t],
  );
  const amountOfDigitsItems = useMemo(
    () =>
      amountOfDigitsOptions.map((amountOfDigits) => ({
        label: t('settings:fields.numberingSeries.format.amountOfDigits.option', { amountOfDigits }),
        value: amountOfDigits,
      })),
    [t],
  );
  const resetIntervalItems = useMemo(
    () =>
      resetIntervalOptions.map((interval) => ({
        label: t(`settings:fields.numberingSeries.resetInterval.options.${interval}`),
        value: interval,
      })),
    [resetIntervalOptions, t],
  );

  useEffect(() => {
    if (!allowMonthlyReset && watchResetInterval === 'monthly') {
      setValue('resetInterval', 'yearly');
    }
  }, [allowMonthlyReset, watchResetInterval, setValue]);

  useEffect(() => {
    if (!showSuffix) {
      setValue('format.suffixSeparator', null);
      setValue('format.suffixYearMonth', null);
      setValue('format.suffixText', null);
    }
  }, [showSuffix, setValue]);

  const onSubmit = handleSubmit((data) => {
    const { format, type, ...rest } = data;

    if (isDuplicateNumberingSeriesFormat({ id: numberingSeries.id, format }, user.numberingSeries)) {
      setError('format.prefixText', { message: t('settings:alerts.numberingSeries.duplicateFormat') });

      return;
    }

    const { enabledForCreditNotes, enabledForInvoices } = getNumberingSeriesEnabledFor(type);

    mutation.mutate(
      {
        ...rest,
        enabledForInvoices,
        enabledForCreditNotes,
        format: {
          amountOfDigits: format.amountOfDigits,
          prefixText: format.prefixText || null,
          prefixYearMonth: format.prefixYearMonth || null,
          prefixSeparator: format.prefixSeparator || null,
          suffixText: format.suffixText || null,
          suffixYearMonth: format.suffixYearMonth || null,
          suffixSeparator: format.suffixSeparator || null,
        },
      },
      {
        onSuccess: () => {
          toast.success({ description: t('settings:alerts.numberingSeries.successUpdated') });
          onClose();
        },
        onError: () => toast.danger({ description: t('common:error') }),
      },
    );
  });

  return (
    <Form className={editFormStyle} onSubmit={onSubmit}>
      <Blocker isBlocked={isDirty} message={t('settings:fields.numberingSeries.blocker')} />

      {section === 'name_and_type' && (
        <fieldset className={nameAndTypeStyle}>
          <Controller
            control={control}
            name="name"
            render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid } }) => (
              <Field.Root invalid={invalid} name={name}>
                <Field.Label infotip={t('settings:fields.numberingSeries.name.tooltip')}>
                  {t('settings:fields.numberingSeries.name.label')}
                </Field.Label>

                <Input
                  onBlur={onBlur}
                  onValueChange={onChange}
                  placeholder={t('settings:fields.numberingSeries.name.placeholder')}
                  ref={ref}
                  value={value}
                />

                <Field.Error match={!!error}>{error?.message}</Field.Error>
              </Field.Root>
            )}
            rules={{
              required: { value: true, message: t('validation:required') },
              validate: { notBlank: (value) => isNotBlank(value) || t('validation:required') },
              maxLength: {
                value: 255,
                message: t('validation:maxLength', { attribute: t('settings:fields.numberingSeries.name.label'), max: 255 }),
              },
            }}
          />

          <Controller
            control={control}
            name="type"
            render={({ field: { name, onChange, value }, fieldState: { error, invalid } }) => (
              <Field.Root invalid={invalid} name={name}>
                <Field.Label infotip={t('settings:fields.numberingSeries.type.tooltip')}>
                  {t('settings:fields.numberingSeries.type.label')}
                </Field.Label>

                <Select.Root items={typeItems} onValueChange={onChange} value={value}>
                  <Select.Button />

                  <Select.List>
                    {typeItems.map((item) => (
                      <Select.Item key={item.value} value={item.value}>
                        {item.label}
                      </Select.Item>
                    ))}
                  </Select.List>
                </Select.Root>

                <Field.Error match={!!error}>{error?.message}</Field.Error>
              </Field.Root>
            )}
            rules={{ required: { value: true, message: t('validation:required') } }}
          />
        </fieldset>
      )}

      {section === 'format_and_options' && (
        <fieldset className={fieldsetStyle}>
          <div>
            <p>
              <Trans i18nKey="settings:fields.numberingSeries.format.nextNumber" values={{ number: previewEntryNumber }} />
            </p>
            {previewEntryNumber.length > 30 && (
              <div className={clsx(statusColorThemes.warning, lengthWarningStyle)}>
                <Icon name="Info" size={18} />
                <p>{t('settings:fields.numberingSeries.format.lengthWarning', { limit: 30 })}</p>
              </div>
            )}
          </div>

          <div className={formatRowStyle}>
            <div className={formatFieldsStyle}>
              <Controller
                control={control}
                name="format.prefixText"
                render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid } }) => (
                  <Field.Root invalid={invalid} name={name}>
                    <Field.Label>{t('settings:fields.numberingSeries.format.prefixText.label')}</Field.Label>

                    <Input onBlur={onBlur} onValueChange={onChange} ref={ref} value={value ?? ''} />

                    <Field.Error match={!!error}>{error?.message}</Field.Error>
                  </Field.Root>
                )}
                rules={{
                  maxLength: {
                    value: 32,
                    message: t('validation:maxLength', {
                      attribute: t('settings:fields.numberingSeries.format.prefixText.label'),
                      max: 32,
                    }),
                  },
                }}
              />

              <Controller
                control={control}
                name="format.prefixYearMonth"
                render={({ field: { name, onChange, value } }) => (
                  <Field.Root name={name}>
                    <Field.Label>{t('settings:fields.numberingSeries.format.year.label')}</Field.Label>

                    <Select.Root items={yearMonthItems} onValueChange={onChange} value={value}>
                      <Select.Button />

                      <Select.List>
                        {yearMonthItems.map((item) => (
                          <Select.Item key={item.value ?? 'none'} value={item.value}>
                            {item.label}
                          </Select.Item>
                        ))}
                      </Select.List>
                    </Select.Root>
                  </Field.Root>
                )}
              />

              <Controller
                control={control}
                name="format.prefixSeparator"
                render={({ field: { name, onChange, value } }) => (
                  <Field.Root name={name}>
                    <Field.Label>{t('settings:fields.numberingSeries.format.separator.label')}</Field.Label>

                    <Select.Root items={separatorItems} onValueChange={onChange} value={value}>
                      <Select.Button />

                      <Select.List>
                        {separatorItems.map((item) => (
                          <Select.Item key={item.value ?? 'none'} value={item.value}>
                            {item.label}
                          </Select.Item>
                        ))}
                      </Select.List>
                    </Select.Root>
                  </Field.Root>
                )}
              />

              <Controller
                control={control}
                name="format.amountOfDigits"
                render={({ field: { name, onChange, value } }) => (
                  <Field.Root name={name}>
                    <Field.Label>{t('settings:fields.numberingSeries.format.amountOfDigits.label')}</Field.Label>

                    <Select.Root items={amountOfDigitsItems} onValueChange={onChange} value={value}>
                      <Select.Button />

                      <Select.List>
                        {amountOfDigitsItems.map((item) => (
                          <Select.Item key={item.value} value={item.value}>
                            {item.label}
                          </Select.Item>
                        ))}
                      </Select.List>
                    </Select.Root>
                  </Field.Root>
                )}
              />
            </div>

            {numberingSeries.isNotUsed && (
              <div className={startAtStyle}>
                <Controller
                  control={control}
                  name="nextNumber"
                  render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid } }) => (
                    <Field.Root invalid={invalid} name={name}>
                      <Field.Label>
                        <span className={startAtLabelStyle}>{t('settings:fields.numberingSeries.startAt.label')}</span>
                      </Field.Label>

                      <NumberInput
                        format={{ maximumFractionDigits: 0, useGrouping: false }}
                        onBlur={onBlur}
                        onValueChange={onChange}
                        ref={ref}
                        step={1}
                        value={value}
                      />

                      <Field.Error match={!!error}>{error?.message}</Field.Error>
                    </Field.Root>
                  )}
                  rules={{
                    required: { value: true, message: t('validation:required') },
                    min: {
                      value: 1,
                      message: t('validation:min', { attribute: t('settings:fields.numberingSeries.startAt.label'), min: 1 }),
                    },
                  }}
                />
              </div>
            )}
          </div>

          <Field.Root className={useSuffixStyle}>
            <Checkbox.Root checked={showSuffix} onCheckedChange={() => toggleShowSuffix()}>
              <Checkbox.Label>{t('settings:fields.numberingSeries.format.useSuffix')}</Checkbox.Label>
            </Checkbox.Root>
          </Field.Root>

          {showSuffix && (
            <div>
              <div className={suffixFieldsStyle}>
                <Controller
                  control={control}
                  name="format.suffixSeparator"
                  render={({ field: { name, onChange, value } }) => (
                    <Field.Root name={name}>
                      <Field.Label>{t('settings:fields.numberingSeries.format.separator.label')}</Field.Label>

                      <Select.Root items={separatorItems} onValueChange={onChange} value={value}>
                        <Select.Button />

                        <Select.List>
                          {separatorItems.map((item) => (
                            <Select.Item key={item.value ?? 'none'} value={item.value}>
                              {item.label}
                            </Select.Item>
                          ))}
                        </Select.List>
                      </Select.Root>
                    </Field.Root>
                  )}
                />

                <Controller
                  control={control}
                  name="format.suffixYearMonth"
                  render={({ field: { name, onChange, value } }) => (
                    <Field.Root name={name}>
                      <Field.Label>{t('settings:fields.numberingSeries.format.year.label')}</Field.Label>

                      <Select.Root items={yearMonthItems} onValueChange={onChange} value={value}>
                        <Select.Button />

                        <Select.List>
                          {yearMonthItems.map((item) => (
                            <Select.Item key={item.value ?? 'none'} value={item.value}>
                              {item.label}
                            </Select.Item>
                          ))}
                        </Select.List>
                      </Select.Root>
                    </Field.Root>
                  )}
                />

                <Controller
                  control={control}
                  name="format.suffixText"
                  render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid } }) => (
                    <Field.Root invalid={invalid} name={name}>
                      <Field.Label>{t('settings:fields.numberingSeries.format.suffixText.label')}</Field.Label>

                      <Input onBlur={onBlur} onValueChange={onChange} ref={ref} value={value ?? ''} />

                      <Field.Error match={!!error}>{error?.message}</Field.Error>
                    </Field.Root>
                  )}
                  rules={{
                    maxLength: {
                      value: 32,
                      message: t('validation:maxLength', {
                        attribute: t('settings:fields.numberingSeries.format.suffixText.label'),
                        max: 32,
                      }),
                    },
                  }}
                />
              </div>
            </div>
          )}
        </fieldset>
      )}

      {section === 'format_and_options' && (
        <fieldset className={fieldsetStyle}>
          <Controller
            control={control}
            name="resetInterval"
            render={({ field: { name, onChange, value } }) => (
              <Field.Root className={resetIntervalRowStyle} name={name}>
                <div>
                  <Field.Label>{t('settings:fields.numberingSeries.resetInterval.label')}</Field.Label>
                  <Field.Description>{t('settings:fields.numberingSeries.resetInterval.description')}</Field.Description>
                </div>

                <Select.Root items={resetIntervalItems} onValueChange={onChange} value={value}>
                  <Select.Button />

                  <Select.List>
                    {resetIntervalItems.map((item) => (
                      <Select.Item key={item.value} value={item.value}>
                        {item.label}
                      </Select.Item>
                    ))}
                  </Select.List>
                </Select.Root>
              </Field.Root>
            )}
          />
        </fieldset>
      )}

      <div className={actionsStyle}>
        <Button disabled={mutation.isPending} emphasis="secondary" onClick={onClose}>
          {t('common:actions.cancel')}
        </Button>

        <Button loading={mutation.isPending} type="submit">
          {t('common:save')}
        </Button>
      </div>
    </Form>
  );
};
